/**
 * Consent Manager for CCPA compliance
 * Manages implicit consent for California users through form interaction
 * Uses CloudFront Lambda@Edge for fast, reliable geolocation detection
 */

class ConsentManager {
  private static instance: ConsentManager;
  private readonly CONSENT_KEY = 'user_interaction_ca';
  private readonly REGION_KEY = 'user_region';
  private readonly GEO_API = '/_geo'; // CloudFront Lambda@Edge endpoint
  private readonly CACHE_EXPIRATION_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
  private isCaliforniaUserCache: boolean | null = null;
  
  private constructor() {}

  public static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  /**
   * Detect if user is from California (cached with 30min expiration)
   */
  public async isCaliforniaUser(): Promise<boolean> {
    // Return cached value if available
    if (this.isCaliforniaUserCache !== null) {
      return this.isCaliforniaUserCache;
    }

    // Check localStorage for previous detection (with expiration)
    const stored = localStorage.getItem(this.REGION_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const age = Date.now() - data.timestamp;
        
        // Check if cache is still valid (within 30 minutes)
        if (age < this.CACHE_EXPIRATION_MS) {
          this.isCaliforniaUserCache = data.region === 'CA';
          return this.isCaliforniaUserCache;
        }
        // Cache expired, will re-fetch below
      } catch (e) {
        // Old format or corrupted data, will re-fetch below
      }
    }

    try {
      // Fetch user's location from CloudFront Lambda@Edge
      const response = await fetch(this.GEO_API, { 
        cache: 'no-store'
      });
      
      if (!response.ok) {
        throw new Error(`Geo API returned ${response.status}`);
      }
      
      const data = await response.json();
      
      // Lambda@Edge returns { country: "US", state: "CA" }
      const isCalifornia = data.country === 'US' && data.state === 'CA';
      
      // Cache the result with timestamp
      this.isCaliforniaUserCache = isCalifornia;
      const cacheData = {
        region: isCalifornia ? 'CA' : 'non-CA',
        timestamp: Date.now()
      };
      localStorage.setItem(this.REGION_KEY, JSON.stringify(cacheData));
      
      return isCalifornia;
    } catch (error) {
      // Default to California if detection fails (conservative approach)
      this.isCaliforniaUserCache = true;
      return true;
    }
  }

  /**
   * Check if user has interacted with the form
   */
  public hasInteracted(): boolean {
    return localStorage.getItem(this.CONSENT_KEY) === 'true';
  }

  /**
   * Record user interaction (implicit consent)
   */
  public recordInteraction(): void {
    try {
      localStorage.setItem(this.CONSENT_KEY, 'true');
      window.dispatchEvent(new CustomEvent('userInteracted'));
    } catch (error) {
      console.error('Error recording interaction:', error);
    }
  }

  /**
   * Check if GTM should load (California users need interaction first)
   */
  public async shouldLoadGTM(): Promise<boolean> {
    const isCalifornia = await this.isCaliforniaUser();
    
    // Non-California users: load immediately
    if (!isCalifornia) {
      return true;
    }
    
    // California users: only load after interaction
    return this.hasInteracted();
  }

  /**
   * Clear interaction state (for testing)
   */
  public clearInteraction(): void {
    localStorage.removeItem(this.CONSENT_KEY);
    localStorage.removeItem(this.REGION_KEY);
    this.isCaliforniaUserCache = null;
  }
}

export const consentManager = ConsentManager.getInstance();

